import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  Input,
  inject,
  computed,
  effect,
  ElementRef
} from '@angular/core';
import { SelectItem } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Services
import { custom } from '@portal/app/shared/services/date-time.service';
import { DropdownService } from '@portal/app/dashboard/home-page/services/dropdown.service';
import { FilterService } from '@portal/app/dashboard/home-page/services/filter.service';
import { HeaderService } from './header.service';
import { UserService } from '@portal/app/dashboard/home-page/services/user.service';

// Components
import { DateRangePickerComponent } from '@portal/app/dashboard/date-range-picker/date-range-picker.component';

// Types
import type {
  DateRange,
  DateRangeData,
  Filter,
  FilterValue
} from '@portal/app/shared/types';
import dayjs from 'dayjs';
import { NativeSectionsService } from '@portal/app/shared/services/native-sections.service';

import { FiscalMonth } from '@design-system/components/m-fiscal-calendar/m-fiscal-calendar.component.types';
import { FiscalCalendarStateService } from '@portal/app/shared/services/fiscal-calendar-state.service';
import { FiscalDatePresetService } from '@portal/app/shared/services/fiscal-date-preset.service';

interface FiscalDateChange {
  startDate: string;
  endDate: string;
  previousStartDate: string | null;
  previousEndDate: string | null;
  presetValue: string;
}

@Component({
  selector: 'portal-homepage-header',
  templateUrl: './header.component.html'
})
export class HomepageHeaderComponent implements OnInit, OnDestroy {
  userName = '';
  title = '';
  compareDateRange: DateRange | null = null;
  compareFilter: Filter | undefined = undefined;
  conversionTypeOptions: SelectItem[] = [];
  conversionType: FilterValue = '';
  relativeDay: FilterValue = '';
  relativeDayFilter: Filter | undefined = undefined;
  relativeDayOptions: SelectItem[] = [];
  selectedDateRange: DateRange | null = null;
  previousRelativeDay: FilterValue = '';
  minDate!: dayjs.Dayjs;

  private destroy$ = new Subject<void>();

  @Input() hasData = false;

  // Components
  @ViewChild('datePicker') datePickerComponent!: DateRangePickerComponent;

  @ViewChild('relativeDayDropdown', { read: ElementRef })
  relativeDayDropdownRef?: ElementRef;

  private fiscalCalendarStateService = inject(FiscalCalendarStateService);
  private fiscalDatePresetService = inject(FiscalDatePresetService);

  openCalendar = false;

  months = computed<FiscalMonth[]>(() => {
    const data = this.fiscalCalendarStateService.fiscalCalendarData();
    return (data?.months ?? []) as FiscalMonth[];
  });

  isCustomCalendar = computed(() =>
    this.fiscalCalendarStateService.isCustomCalendarEnabled()
  );

  readonly fiscalMaxDate = dayjs().subtract(1, 'day');

  fiscalRelativeDayOptions = computed<SelectItem[]>(() => {
    return this.fiscalDatePresetService.datePresetOptions.map((option) => ({
      label: option.label,
      value: option.value
    }));
  });

  constructor(
    private dropdownService: DropdownService,
    private filterService: FilterService,
    private headerService: HeaderService,
    private userService: UserService,
    public readonly nativeSectionsService: NativeSectionsService
  ) {
    effect(() => {
      this.initializeDropdownOptions();
      const rdValue = this.filterService
        .getCurrentFilters()
        .find((f) => f.literalId === 'relative_day')?.value as
        | string
        | undefined;
      if (rdValue) {
        Promise.resolve().then(() => {
          this.relativeDay = rdValue;
        });
      }
    });
  }

  ngOnInit() {
    this.initializeUser();
    this.initializeDropdownOptions();
    this.subscribeToServiceObservables();
  }

  private initializeUser() {
    this.userService.fetchAndSetUserName();
    this.userService.userName
      .pipe(takeUntil(this.destroy$))
      .subscribe((userName) => {
        this.userName = userName;
        this.title = this.getTitle(this.userName);
      });
  }

  private initializeDropdownOptions() {
    this.relativeDayOptions = this.isCustomCalendar()
      ? this.fiscalRelativeDayOptions()
      : this.dropdownService.getRelativeDayOptions();
  }

  private subscribeToServiceObservables() {
    this.subscribeToDashboardState();
    this.subscribeToConversionTypeOptions();
    this.subscribeToSelectedConversionType();
    this.subscribeToSelectedRelativeDay();
    this.subscribeToSelectedDateRange();
    this.subscribeToCompareDateRange();
    this.subscribeToCompareFilter();
  }

  private subscribeToConversionTypeOptions() {
    this.headerService.conversionTypeOptions$
      .pipe(takeUntil(this.destroy$))
      .subscribe((options) => (this.conversionTypeOptions = options));
  }

  private subscribeToSelectedConversionType() {
    this.headerService.selectedConversionType$
      .pipe(takeUntil(this.destroy$))
      .subscribe((type) => (this.conversionType = type));
  }

  private subscribeToSelectedRelativeDay() {
    this.headerService.selectedRelativeDay$
      .pipe(takeUntil(this.destroy$))
      .subscribe((relativeDay) => {
        this.relativeDayFilter = relativeDay;
        this.previousRelativeDay = relativeDay?.value || '';
        this.relativeDay = relativeDay?.value || '';
      });
  }

  private subscribeToSelectedDateRange() {
    this.headerService.selectedDateRange$
      .pipe(takeUntil(this.destroy$))
      .subscribe((dateRange) => (this.selectedDateRange = dateRange));
  }

  private subscribeToCompareDateRange() {
    this.headerService.compareDateRange$
      .pipe(takeUntil(this.destroy$))
      .subscribe((compareDateRange) => {
        this.compareDateRange = compareDateRange;
      });
  }

  private subscribeToCompareFilter() {
    this.headerService.compareFilter$
      .pipe(takeUntil(this.destroy$))
      .subscribe((compareFilter) => {
        this.compareFilter = compareFilter;
      });
  }

  private subscribeToDashboardState() {
    this.headerService.dashboardState$
      .pipe(takeUntil(this.destroy$))
      .subscribe((dashboardState) => {
        this.minDate = this.nativeSectionsService.getMinDate(
          dashboardState?.reportingRangeInMonths
        );
      });
  }

  onRelativeDayChange(value: string) {
    if (value === custom || value === 'custom') {
      this.openCalendar = true;
    } else {
      this.headerService.setRelativeDay(value);
    }
  }

  onConversionTypeChange(conversionType: string) {
    this.headerService.setConversionType(conversionType);
  }

  navigateToSupportPage(): void {
    window.open(
      'https://support.measured.com/article/ng7ezt7tuv-homepage-using-your-measured-homepage',
      '_blank'
    );
  }

  onCustomDateChange(dateRangeData: DateRangeData[]) {
    this.headerService.updateDateFilters(dateRangeData);
    this.headerService.setDateFilters(dateRangeData);
    const selectedCompareOptionValue = dateRangeData.find(
      (data) => data.filterLiteralId === 'selected_compare_option'
    )?.value as string | undefined;

    if (selectedCompareOptionValue) {
      // Update selected compare option upon save
      this.filterService.setSelectedCompareOption(selectedCompareOptionValue);
    }
  }

  handleCancel() {
    this.relativeDay = this.previousRelativeDay;
  }

  // Handle "Custom" option being clicked
  onDropdownClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const customLabel = target.innerText?.trim()?.toLowerCase();
    const ariaLabel = target.getAttribute('aria-label')?.toLowerCase();

    const isCustomSpan =
      target.tagName === 'SPAN' &&
      target.classList.length === 1 &&
      target.classList.contains('ng-star-inserted') &&
      customLabel === 'custom';

    const isCustomLabel = target.tagName === 'LI' && ariaLabel === 'custom';

    // Check if the target is custom option
    if (isCustomSpan || isCustomLabel) {
      if (this.isCustomCalendar()) {
        this.openCalendar = true;
      } else {
        this.datePickerComponent.showCalendar = true;
      }
    }
  }

  handleCompareOptionChange(selectedCompareOptionName: string): void {
    this.filterService.setSelectedCompareOption(selectedCompareOptionName);
  }

  handleFiscalCalendarCancel(): void {
    this.openCalendar = false;
    this.relativeDay = this.previousRelativeDay;
  }

  handleFiscalDateChange(dateChange: FiscalDateChange): void {
    this.openCalendar = false;
    const dateRangeData: DateRangeData[] = [
      {
        name: 'day',
        value: [dateChange.startDate, dateChange.endDate],
        filterLiteralId: 'day',
        type: 'string'
      },
      {
        name: 'relativeDate',
        value:
          dateChange.presetValue === 'Custom'
            ? 'Custom'
            : dateChange.presetValue,
        filterLiteralId: 'relative_day',
        type: 'string'
      }
    ];

    if (dateChange.previousStartDate && dateChange.previousEndDate) {
      dateRangeData.push({
        name: 'compareDate',
        value: [dateChange.previousStartDate, dateChange.previousEndDate],
        filterLiteralId: 'compare_date',
        type: 'string'
      });
    }

    this.headerService.updateDateFilters(dateRangeData);
    this.headerService.setDateFilters(dateRangeData);
  }

  getDateRangeStart(): string {
    return this.selectedDateRange?.startDate
      ? dayjs(this.selectedDateRange.startDate).format('YYYY-MM-DD')
      : '';
  }

  getDateRangeEnd(): string {
    return this.selectedDateRange?.endDate
      ? dayjs(this.selectedDateRange.endDate).format('YYYY-MM-DD')
      : '';
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getTitle(userName: string) {
    let title = `Welcome, ${userName}`;
    if (!userName || userName === 'Unknown User') {
      title = 'Welcome to Measured';
    }
    return title;
  }
}
